import React from 'react';
import {
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { dataFormStyles } from '~/modules/covenants/CovenantsDataForm/styles.ts';
import { guaranteesClientText } from '~/modules/guarantees/form/client/constants.ts';
import { guaranteesClientPageStyles } from '~/modules/guarantees/form/client/styles.ts';
import { getGuaranteesContractIdentityKey } from '../../../rootSection/contractIdentity.ts';
import type { TGuaranteesContractOption } from '../../../rootSection/types.ts';
import { areSameContractOptions } from '../selectionUtils.ts';
import MainDealTableRow from './MainDealTableRow.tsx';

interface IMainDealTableProps {
  contractOptions: TGuaranteesContractOption[];
  isDisabled: boolean;
  isLoading: boolean;
  selectedContract: TGuaranteesContractOption | null;
  onContractSelect: (option: TGuaranteesContractOption) => void;
}

const MainDealTable: React.FC<IMainDealTableProps> = ({
  contractOptions,
  isDisabled,
  isLoading,
  selectedContract,
  onContractSelect,
}) => (
  <TableContainer sx={dataFormStyles.documentTableContainer}>
    <Table
      sx={
        [
          dataFormStyles.documentTable,
          guaranteesClientPageStyles.flowBusinessTable,
        ] as SxProps<Theme>
      }
    >
      <TableHead sx={dataFormStyles.documentTableHead}>
        <TableRow>
          <TableCell sx={(theme) => dataFormStyles.documentTableHeadCell(theme, 'name')}>
            {guaranteesClientText.mainDealNumberLabel}
          </TableCell>
          <TableCell sx={(theme) => dataFormStyles.documentTableHeadCell(theme, 'createdAt')}>
            {guaranteesClientText.mainDealDateLabel}
          </TableCell>
          <TableCell sx={(theme) => dataFormStyles.documentTableHeadCell(theme, 'period')}>
            {guaranteesClientText.mainDealDateFromLabel}
          </TableCell>
          <TableCell sx={(theme) => dataFormStyles.documentTableHeadCell(theme, 'period')}>
            {guaranteesClientText.mainDealDateToLabel}
          </TableCell>
          <TableCell align="center" sx={(theme) => dataFormStyles.documentTableHeadCell(theme, 'status')}>
            {guaranteesClientText.mainDealCurrencyLabel}
          </TableCell>
          <TableCell align="right" sx={(theme) => dataFormStyles.documentTableHeadCell(theme, 'amount')}>
            {guaranteesClientText.mainDealAmountLabel}
          </TableCell>
          <TableCell align="right" sx={(theme) => dataFormStyles.documentTableHeadCell(theme, 'amount')}>
            {guaranteesClientText.mainDealLimitBalanceLabel}
          </TableCell>
          <TableCell align="center" sx={(theme) => dataFormStyles.documentTableHeadCell(theme, 'actions')}>
            {guaranteesClientText.actionsLabel}
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {isLoading ? (
          <TableRow>
            <TableCell colSpan={8} align="center" sx={dataFormStyles.documentEmptyCell}>
              <Box sx={guaranteesClientPageStyles.flowLoadingCell}>
                <CircularProgress size={24} />
                {guaranteesClientText.contractLoadingText}
              </Box>
            </TableCell>
          </TableRow>
        ) : !contractOptions.length ? (
          <TableRow>
            <TableCell colSpan={8} align="center" sx={dataFormStyles.documentEmptyCell}>
              {guaranteesClientText.contractNoOptionsText}
            </TableCell>
          </TableRow>
        ) : (
          contractOptions.map((option, index) => (
            <MainDealTableRow
              key={getGuaranteesContractIdentityKey(option) || option.value || index}
              isDisabled={isDisabled}
              isSelected={!!selectedContract && areSameContractOptions(selectedContract, option)}
              option={option}
              onContractSelect={onContractSelect}
            />
          ))
        )}
      </TableBody>
    </Table>
  </TableContainer>
);

export default MainDealTable;
